/** @jsxImportSource @emotion/react */
import { Fragment, useEffect, useMemo, useRef } from "react";
import { css } from "@emotion/react";
import { useSelector } from "react-redux";
import { IAppState } from "../../../core/redux/IAppState";
import { getResource } from "../../../language/resources/getResource";
import expandIcon from "../../../assets/icons/circlePlus.svg";
import collapseIcon from "../../../assets/icons/circleMinus.svg";
import {
  DARK_BLUE_CREEK,
  COOLER_GREY,
  DARK_GREY,
  DARK_RED_CREEK,
  IKON_BLUE,
  LIGHT_GREY,
  WHITE,
} from "../../../styles/colors";
import { LiftAccessRuleSet } from "./models";
import {
  SHARED_MARK,
  effectiveStartLines,
  effectiveEndLines,
  effectiveTimeLines,
  maxDaysLines,
  blackoutRangeLines,
  blackoutDowLines,
} from "./ruleSetDisplay";

type Resources = Record<string, any>;

// Turns a trailing shared-usage marker into a styled asterisk with a tooltip.
function renderValueLines(text: string, resources: Resources) {
  const sharedTitle = getResource(resources, "LIFT_ACCESS_RULES_TABLE_SHARED");
  return text.split("\n").map((line, i) => {
    const shared = line.endsWith(SHARED_MARK);
    const base = shared ? line.slice(0, -SHARED_MARK.length) : line;
    return (
      <Fragment key={i}>
        {i > 0 && <br />}
        {base}
        {shared && (
          <span className="shared-star" title={sharedTitle}>
            {SHARED_MARK}
          </span>
        )}
      </Fragment>
    );
  });
}

type Column = {
  key: string;
  labelKey: string;
  align: "left" | "center";
  /** Exact width for pinned columns; a floor for grow columns (see GROW_COLUMNS). */
  width: number;
  /** Cells may wrap onto extra lines; otherwise values are kept on one line. */
  wrap?: boolean;
  ellipsis?: boolean;
  value: (rs: LiftAccessRuleSet, ctx: Ctx) => string;
};

export type Ctx = {
  dateFormat: string;
  timeFormat: string;
  resources: Resources;
};

const lines = (arr: string[]) => arr.join("\n");

const COLUMNS: Column[] = [
  {
    key: "id",
    labelKey: "LIFT_ACCESS_RULES_TABLE_ID",
    align: "center",
    width: 56,
    value: (rs) => String(rs.entitlementTypeId),
  },
  {
    key: "description",
    labelKey: "LIFT_ACCESS_RULES_TABLE_DESCRIPTION",
    align: "left",
    width: 190,
    // Wraps rather than truncating: worth reading in full before deleting.
    wrap: true,
    value: (rs) => rs.entitlementDescription,
  },
  {
    key: "season",
    labelKey: "LIFT_ACCESS_RULES_TABLE_SEASON",
    align: "center",
    // Fits "2026-2027".
    width: 86,
    value: (rs) => rs.sellingSeasonDescription,
  },
  {
    key: "fulfillmentSeason",
    labelKey: "LIFT_ACCESS_RULES_TABLE_FULFILLMENT_SEASON",
    align: "left",
    // Sized to the header, which is wider than the values.
    width: 160,
    value: (rs) => rs.fulfillmentSeasonDescription,
  },
  {
    key: "location",
    labelKey: "LIFT_ACCESS_RULES_TABLE_LOCATION",
    align: "left",
    width: 150,
    ellipsis: true,
    value: (rs) => rs.locationName,
  },
  {
    key: "start",
    labelKey: "LIFT_ACCESS_RULES_TABLE_EFFECTIVE_DATE",
    align: "center",
    // Fits a full MM/DD/YYYY.
    width: 96,
    value: (rs, c) => lines(effectiveStartLines(rs, c.dateFormat)),
  },
  {
    key: "end",
    labelKey: "LIFT_ACCESS_RULES_TABLE_EXPIRATION_DATE",
    align: "center",
    width: 96,
    value: (rs, c) => lines(effectiveEndLines(rs, c.dateFormat)),
  },
  {
    key: "time",
    labelKey: "LIFT_ACCESS_RULES_TABLE_EFFECTIVE_TIME",
    align: "center",
    width: 108,
    value: (rs, c) => lines(effectiveTimeLines(rs, c.timeFormat)),
  },
  {
    key: "maxDays",
    labelKey: "LIFT_ACCESS_RULES_TABLE_MAX_DAYS",
    align: "center",
    // Fits the "Max Days" header, wider than the values.
    width: 88,
    value: (rs) => lines(maxDaysLines(rs)),
  },
  {
    key: "blackoutRange",
    labelKey: "LIFT_ACCESS_RULES_TABLE_BLACKOUT",
    align: "left",
    // Floor: holds one whole "date – date (time – time)" entry per line.
    width: 340,
    wrap: true,
    value: (rs, c) => lines(blackoutRangeLines(rs, c.dateFormat, c.timeFormat)),
  },
  {
    key: "blackoutDow",
    labelKey: "LIFT_ACCESS_RULES_TABLE_BLACKOUT_DOW",
    align: "left",
    // Holds the worst case: seven short day names plus a time range.
    width: 300,
    wrap: true,
    value: (rs, c) => lines(blackoutDowLines(rs, c.resources, c.timeFormat)),
  },
];

// Left auto-width so they split the table's leftover space; every other column is
// pinned. With all columns shown only Description has slack worth giving; with the
// detail columns hidden there is far more, so it spreads across four.
const GROW_COLUMNS = {
  all: ["description"],
  identity: ["description", "season", "fulfillmentSeason", "location"],
};

const LAST_IDENTITY_COLUMN = "location";
const IDENTITY_COLUMNS = COLUMNS.slice(
  0,
  COLUMNS.findIndex((c) => c.key === LAST_IDENTITY_COLUMN) + 1,
);

const totalWidth = (cols: Column[]) =>
  cols.reduce((total, col) => total + col.width, 0);

/** What the grid wants to be in each mode, so the host dialog can size itself to it. */
export const SUMMARY_GRID_WIDTH = {
  identity: totalWidth(IDENTITY_COLUMNS),
  all: totalWidth(COLUMNS),
};

/** A cell line: 12px text at line-height 1.45, rounded up. */
const GRID_LINE_PX = 18;

/** A cell's padding, top and bottom, plus the row separator. */
const GRID_ROW_CHROME_PX = 17;

/** The header row: one line of 14px text. */
const GRID_HEADER_PX = 21 + GRID_ROW_CHROME_PX;

/** Lines a cell renders. `renderValueLines` splits on newlines. */
const linesIn = (text: string) => (text ? text.split("\n").length : 1);

/**
 * The height the grid wants, for the host dialog to size to. Derived from the lines each
 * cell renders, as SUMMARY_GRID_WIDTH sums declared widths rather than measuring.
 */
export const summaryGridHeightPx = (
  ruleSets: LiftAccessRuleSet[],
  showAllColumns: boolean,
  ctx: Ctx,
): number => {
  const columns = showAllColumns ? COLUMNS : IDENTITY_COLUMNS;
  return (
    GRID_HEADER_PX +
    ruleSets.reduce((total, rs) => {
      const lines = columns.map((col) => linesIn(col.value(rs, ctx)));
      return total + Math.max(1, ...lines) * GRID_LINE_PX + GRID_ROW_CHROME_PX;
    }, 0)
  );
};

// Fills its container rather than pinning a width — the host dialog owns sizing.
const rootCss = css`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
  min-width: 0;
`;

// Applied only while the grid is open, so it inherits the leftover height instead
// of the dialog scrolling. min-height: 0 is what lets these boxes give it up.
const fillCss = css`
  flex: 1 1 auto;
  min-height: 0;
`;

// overflow: hidden clips the excess when a short window squeezes the grid past its
// floor, rather than painting it over the rows below.
const gridSectionCss = css`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const warningCss = css`
  color: ${DARK_RED_CREEK};
  font-weight: 600;
  font-size: 15px;
  margin-bottom: 10px;
  flex-shrink: 0;
`;

const summaryHeaderCss = css`
  display: flex;
  align-items: center;
  gap: 28px;
  flex-shrink: 0;
`;

const toggleCss = css`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: ${IKON_BLUE};

  .expand-icon {
    display: inline-block;
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }
`;

const showAllColumnsCss = css`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  flex: 0 0 auto;
  cursor: pointer;
  font-size: 13px;

  /* Quieter than the expand toggle beside it: this is a view option. */
  font-weight: 400;
  color: ${DARK_GREY};
  user-select: none;

  input {
    cursor: pointer;
    margin: 0;

    /* Explicit box so centering doesn't depend on the UA's default metrics. */
    width: 14px;
    height: 14px;
    flex: 0 0 auto;
  }

  /* Collapse the line box to the glyphs, or the text sits low beside the checkbox. */
  span {
    line-height: 1;
  }
`;

// Applied while the grid is collapsed, when there are no columns to show yet.
const showAllColumnsDisabledCss = css`
  cursor: default;
  opacity: 0.5;

  input {
    cursor: default;
  }
`;

// The only thing in the dialog that scrolls: takes the spare height so the dialog
// never has to. The floor is viewport-relative so it can't outgrow a short window.
// No border — it would box in the sticky header; the outline is on the body cells.
const gridScrollCss = css`
  margin-top: 16px;
  overflow: auto;
  flex: 1 1 auto;
  min-height: min(140px, 20vh);
`;

const gridTableCss = css`
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;

  /* Columns derive from the header widths, not row content — required alongside
     content-visibility on rows. */
  table-layout: fixed;
  font-size: 12px;

  th,
  td {
    /* COLUMNS widths include the padding; without this every pinned column eats
       20px more than it declares and the grow column gets the crumbs. */
    box-sizing: border-box;
    padding: 8px 10px;
    text-align: left;
    vertical-align: top;

    /* Blackout cells stack several ranges per cell, so leading matters. */
    line-height: 1.45;
    white-space: pre-line;
    overflow-wrap: break-word;
  }

  /* Matches the Access Overview headers, not a small uppercase grey eyebrow. */
  thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: ${WHITE};
    font-size: 14px;
    font-weight: 700;
    letter-spacing: normal;
    text-transform: none;
    color: ${IKON_BLUE};
    white-space: nowrap;
  }

  /* COOLER_GREY, not LIGHT_GREY — that's the zebra colour and read as no border. */
  tbody td {
    border-bottom: 1px solid ${COOLER_GREY};
    color: ${IKON_BLUE};
  }

  /* Outline drawn by the body cells so it encloses the rows but not the headers. */
  tbody tr:first-of-type td {
    border-top: 1px solid ${COOLER_GREY};
  }

  tbody td:first-of-type {
    border-left: 1px solid ${COOLER_GREY};
  }

  tbody td:last-of-type {
    border-right: 1px solid ${COOLER_GREY};
  }

  /* Skips layout/paint for off-screen rows so a large set doesn't stall on expand. */
  tbody tr {
    background-color: ${WHITE};
    content-visibility: auto;
    contain-intrinsic-size: auto 40px;
  }

  tbody tr:nth-of-type(odd) {
    background-color: ${LIGHT_GREY};
  }

  .col-center {
    text-align: center;
  }

  /* "pre", not "nowrap": cells still stack one line per season, they just never
     wrap a date or time mid-value. */
  .no-wrap {
    white-space: pre;
  }

  /* No max-width: the column definition sets the width, and it would fight the growers. */
  .ellipsis {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .shared-star {
    color: ${DARK_BLUE_CREEK};
    font-weight: 700;
    font-size: 15px;
    margin-left: 1px;
    cursor: help;
  }
`;

/** Both view toggles are owned by the host dialog so they survive a remount here. */
type Props = {
  ruleSets: LiftAccessRuleSet[];
  /** Collapsed by default so a large deletion set doesn't dominate the dialog. */
  gridOpen: boolean;
  onGridOpenChange: (open: boolean) => void;
  /** Off by default: identity columns answer "which rule sets?"; detail is opt-in. */
  showAllColumns: boolean;
  onShowAllColumnsChange: (show: boolean) => void;
  /** The height the grid wants, or 0 while it is collapsed. */
  onGridHeightChange?: (px: number) => void;
};

/** Delete-confirmation body: which rule sets, and their rules, are about to go. */
export function BulkDeleteSummary({
  ruleSets,
  gridOpen,
  onGridOpenChange,
  showAllColumns,
  onShowAllColumnsChange,
  onGridHeightChange,
}: Props) {
  const resources = useSelector((s: IAppState) => s.locale);
  const dateFormat = useSelector((s: IAppState) => s.defaultDateFormat);
  const timeFormat = useSelector((s: IAppState) => s.defaultTimeFormat);

  // Closing clears the column choice: the checkbox is unreachable while closed, and it
  // still drives the dialog width.
  const toggleGrid = () => {
    const open = !gridOpen;
    onGridOpenChange(open);
    if (!open) onShowAllColumnsChange(false);
  };

  const ctx: Ctx = useMemo(
    () => ({ dateFormat, timeFormat, resources }),
    [dateFormat, timeFormat, resources],
  );

  // Computed, not measured: a measurement is window-relative, so docked and popped-out
  // readings of the same grid disagree and a size remembered against one never matches.
  const gridHeight = useMemo(
    () => (gridOpen ? summaryGridHeightPx(ruleSets, showAllColumns, ctx) : 0),
    [gridOpen, showAllColumns, ruleSets, ctx],
  );

  const onGridHeightChangeRef = useRef(onGridHeightChange);
  onGridHeightChangeRef.current = onGridHeightChange;

  useEffect(() => {
    onGridHeightChangeRef.current?.(gridHeight);
  }, [gridHeight]);

  const columns = showAllColumns ? COLUMNS : IDENTITY_COLUMNS;
  const growKeys = showAllColumns ? GROW_COLUMNS.all : GROW_COLUMNS.identity;

  // Below this the container scrolls sideways rather than squeezing the growers.
  const gridMinWidth = totalWidth(columns);

  return (
    <div css={[rootCss, gridOpen && fillCss]}>
      <div css={warningCss}>
        {getResource(
          resources,
          "ACCESS_RULE_DELETE_SUMMARY_WARNING",
          ruleSets.length,
        )}
      </div>

      <div css={[gridSectionCss, gridOpen && fillCss]}>
        <div css={summaryHeaderCss}>
          <button
            type="button"
            css={toggleCss}
            aria-expanded={gridOpen}
            aria-controls="bulk-delete-affected-grid"
            onClick={toggleGrid}
          >
            <img
              className="expand-icon"
              src={gridOpen ? collapseIcon : expandIcon}
              alt=""
            />
            <span>
              {getResource(
                resources,
                "ACCESS_RULE_DELETE_SUMMARY_TOGGLE",
                ruleSets.length,
              )}
            </span>
          </button>

          <label
            css={[showAllColumnsCss, !gridOpen && showAllColumnsDisabledCss]}
          >
            <input
              type="checkbox"
              checked={showAllColumns}
              // Only actionable once there is a grid to widen.
              disabled={!gridOpen}
              onChange={(e) => onShowAllColumnsChange(e.target.checked)}
            />
            <span>
              {getResource(resources, "ACCESS_RULE_SUMMARY_SHOW_ALL_COLUMNS")}
            </span>
          </label>
        </div>

        {gridOpen && (
          <div id="bulk-delete-affected-grid" css={gridScrollCss}>
            <table css={gridTableCss} style={{ minWidth: gridMinWidth }}>
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      scope="col"
                      className={
                        col.align === "center" ? "col-center" : undefined
                      }
                      // Growers stay auto-width; the rest are pinned.
                      style={
                        growKeys.includes(col.key)
                          ? undefined
                          : { width: col.width }
                      }
                    >
                      {getResource(resources, col.labelKey)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ruleSets.map((rs, rowIdx) => (
                  <tr key={rowIdx}>
                    {columns.map((col) => {
                      const value = col.value(rs, ctx);
                      return (
                        <td
                          key={col.key}
                          className={[
                            col.align === "center" ? "col-center" : "",
                            col.ellipsis ? "ellipsis" : "",
                            // Fixed-format values never break mid-value; the grid
                            // scrolls sideways instead.
                            col.wrap ? "" : "no-wrap",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          title={col.ellipsis ? value : undefined}
                        >
                          {renderValueLines(value, resources)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
